import type { TLaunchpadIconKey, TLaunchpadLaneId } from './interfaces/ILaunchpadLane';

export interface ILaunchpadLaneIndicatorMetric {
  labelI18nKey: string,
  value: string,
}

export interface ILaunchpadLaneIndicatorHorizon {
  monthsI18nKey: string,
  count: number,
  pct: number,
}

export interface ILaunchpadLaneIndicator {
  id: string,
  titleI18nKey: string,
  iconKey: TLaunchpadIconKey,
  metrics: ILaunchpadLaneIndicatorMetric[],
  horizons?: ILaunchpadLaneIndicatorHorizon[],
}

// TODO: API — replace static demo metrics with backend per-lane indicator feeds
const LAUNCHPAD_LANE_INDICATORS: Record<TLaunchpadLaneId, ILaunchpadLaneIndicator[]> = {
  visualize: [
    {
      id: 'map',
      titleI18nKey: 'launchpad.tiles.map',
      iconKey: 'route-tracking',
      metrics: [
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.visualize.map.mappedEntities', value: '214' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.visualize.map.tierCoverage', value: '1–4' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.visualize.map.geoNodes', value: '32 countries' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.visualize.map.lastSync', value: '12 min ago' },
      ],
    },
    {
      id: 'discover',
      titleI18nKey: 'launchpad.tiles.discover',
      iconKey: 'network-ai',
      metrics: [
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.visualize.discover.sessions', value: '18' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.visualize.discover.newLinks', value: '74' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.visualize.discover.highConfidence', value: '61' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.visualize.discover.pendingReview', value: '9' },
      ],
    },
    {
      id: 'collaborate',
      titleI18nKey: 'launchpad.tiles.collaborate',
      iconKey: 'inbound-consolidation',
      metrics: [
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.visualize.collaborate.contributors', value: '12' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.visualize.collaborate.comments', value: '86' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.visualize.collaborate.workspaces', value: '4' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.visualize.collaborate.openTasks', value: '17' },
      ],
    },
  ],
  understand: [
    {
      id: 'finance',
      titleI18nKey: 'launchpad.tiles.finance',
      iconKey: 'financial-risk-roi',
      metrics: [
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.understand.finance.ownedProducts', value: '50' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.understand.finance.revenueExposure', value: 'EUR 60B' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.understand.finance.clientsImpacted', value: '128' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.understand.finance.supplierTiers', value: 'Tier 1–3' },
      ],
    },
    {
      id: 'reputation',
      titleI18nKey: 'launchpad.tiles.reputation',
      iconKey: 'system-health',
      metrics: [
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.understand.reputation.riskSignals', value: '12' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.understand.reputation.sentimentIndex', value: '4.2 / 5' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.understand.reputation.mediaMentions', value: '18' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.understand.reputation.clientSegments', value: '9' },
      ],
    },
    {
      id: 'ownership',
      titleI18nKey: 'launchpad.tiles.ownership',
      iconKey: 'structure-pyramid',
      metrics: [
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.understand.ownership.parentEntities', value: '34' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.understand.ownership.disclosedOwnership', value: '72%' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.understand.ownership.crossHoldings', value: '15' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.understand.ownership.jurisdictions', value: '6' },
      ],
    },
    {
      id: 'regulation',
      titleI18nKey: 'launchpad.tiles.regulation',
      iconKey: 'compliance-document',
      metrics: [
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.understand.regulation.activeRegulations', value: '23' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.understand.regulation.highImpact', value: '8' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.understand.regulation.entitiesAffected', value: '14' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.understand.regulation.complianceGaps', value: '5' },
      ],
      horizons: [
        {
          monthsI18nKey: 'launchpad.indicatorsPanel.indicators.understand.regulation.horizons.threeMo',
          count: 4,
          pct: 28,
        },
        {
          monthsI18nKey: 'launchpad.indicatorsPanel.indicators.understand.regulation.horizons.sixMo',
          count: 7,
          pct: 50,
        },
        {
          monthsI18nKey: 'launchpad.indicatorsPanel.indicators.understand.regulation.horizons.nineMo',
          count: 11,
          pct: 78,
        },
        {
          monthsI18nKey: 'launchpad.indicatorsPanel.indicators.understand.regulation.horizons.twelveMo',
          count: 14,
          pct: 100,
        },
      ],
    },
    {
      id: 'climate',
      titleI18nKey: 'launchpad.tiles.climate',
      iconKey: 'sustainability',
      metrics: [
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.understand.climate.alignmentGap', value: '2.4°C' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.understand.climate.scope3Hotspots', value: '41 tCO₂e' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.understand.climate.physicalRiskSites', value: '22' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.understand.climate.suppliersWithTargets', value: '67%' },
      ],
    },
  ],
  plan: [
    {
      id: 'scenario',
      titleI18nKey: 'launchpad.tiles.scenario',
      iconKey: 'decision-flow',
      metrics: [
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.plan.scenario.modeled', value: '11' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.plan.scenario.sensitivityRuns', value: '37' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.plan.scenario.stressPathways', value: '6' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.plan.scenario.bestFitRoute', value: 'S4' },
      ],
    },
    {
      id: 'prioritize',
      titleI18nKey: 'launchpad.tiles.prioritize',
      iconKey: 'data-trends',
      metrics: [
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.plan.prioritize.initiativesRanked', value: '28' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.plan.prioritize.highPriority', value: '9' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.plan.prioritize.expectedRoi', value: '18%' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.plan.prioritize.ownerAssigned', value: '24' },
      ],
    },
  ],
  act: [
    {
      id: 'awareness',
      titleI18nKey: 'launchpad.tiles.awareness',
      iconKey: 'monitoring-radar',
      metrics: [
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.act.awareness.liveAlerts', value: '7' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.act.awareness.criticalWatchpoints', value: '3' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.act.awareness.escalations', value: '5' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.act.awareness.responseSla', value: '93%' },
      ],
    },
    {
      id: 'sourcing',
      titleI18nKey: 'launchpad.tiles.sourcing',
      iconKey: 'outbound-distribution',
      metrics: [
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.act.sourcing.alternativeSuppliers', value: '42' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.act.sourcing.dualSourceCoverage', value: '58%' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.act.sourcing.avgLeadTime', value: '9.4 d' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.act.sourcing.contractRenewals', value: '14' },
      ],
    },
    {
      id: 'competitors',
      titleI18nKey: 'launchpad.tiles.competitors',
      iconKey: 'supply-chain-node',
      metrics: [
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.act.competitors.benchmarkedPeers', value: '12' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.act.competitors.capabilityGaps', value: '8' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.act.competitors.threatSignals', value: '19' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.act.competitors.opportunityFlags', value: '6' },
      ],
    },
  ],
  reporting: [
    {
      id: 'board-report',
      titleI18nKey: 'launchpad.tiles.boardReport',
      iconKey: 'compliance-document',
      metrics: [
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.reporting.boardReport.latestVersion', value: 'Q3-2026' },
        {
          labelI18nKey: 'launchpad.indicatorsPanel.indicators.reporting.boardReport.sectionsComplete',
          value: '14 / 16',
        },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.reporting.boardReport.reviewComments', value: '22' },
        {
          labelI18nKey: 'launchpad.indicatorsPanel.indicators.reporting.boardReport.approvalStatus',
          value: 'In review',
        },
      ],
    },
    {
      id: 'exec-summary',
      titleI18nKey: 'launchpad.tiles.execSummary',
      iconKey: 'data-trends',
      metrics: [
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.reporting.execSummary.drafts', value: '9' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.reporting.execSummary.avgReadTime', value: '4 min' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.reporting.execSummary.recipients', value: '13' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.reporting.execSummary.openActions', value: '5' },
      ],
    },
    {
      id: 'report-builder',
      titleI18nKey: 'launchpad.tiles.reportBuilder',
      iconKey: 'layers',
      metrics: [
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.reporting.reportBuilder.reusableBlocks', value: '31' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.reporting.reportBuilder.templateVariants', value: '12' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.reporting.reportBuilder.dataSources', value: '18' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.reporting.reportBuilder.syncFreshness', value: '99.1%' },
      ],
    },
    {
      id: 'templated-reports',
      titleI18nKey: 'launchpad.tiles.templatedReports',
      iconKey: 'compliance-document',
      metrics: [
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.reporting.templatedReports.published', value: '24' },
        {
          labelI18nKey: 'launchpad.indicatorsPanel.indicators.reporting.templatedReports.autoSchedules',
          value: '16',
        },
        {
          labelI18nKey: 'launchpad.indicatorsPanel.indicators.reporting.templatedReports.exportFormats',
          value: 'PDF/XLS/CSV',
        },
        {
          labelI18nKey: 'launchpad.indicatorsPanel.indicators.reporting.templatedReports.deliverySuccess',
          value: '97%',
        },
      ],
    },
    {
      id: 'materiality',
      titleI18nKey: 'launchpad.tiles.materiality',
      iconKey: 'health-tech-ecg',
      metrics: [
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.reporting.materiality.activeAssessments', value: '3' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.reporting.materiality.stakeholdersMapped', value: '127' },
        { labelI18nKey: 'launchpad.indicatorsPanel.indicators.reporting.materiality.topicsScored', value: '42' },
        {
          labelI18nKey: 'launchpad.indicatorsPanel.indicators.reporting.materiality.lastPublication',
          value: 'Jun 2026',
        },
      ],
    },
  ],
};

export function getLaunchpadLaneIndicators(laneId: TLaunchpadLaneId): ILaunchpadLaneIndicator[] {
  return LAUNCHPAD_LANE_INDICATORS[laneId];
}

export default LAUNCHPAD_LANE_INDICATORS;
